import { useEffect, useState } from "react";
import { CalendarDays, Mail, Receipt, AlertTriangle, ArrowRight } from "lucide-react";
import { adminDashboard } from "@/lib/api";
import { statusMeta, fmtDate } from "./constants";

export default function Dashboard({ token, onJumpTo }) {
  const [data, setData] = useState(null);

  useEffect(() => { adminDashboard(token).then(setData).catch(() => {}); }, [token]);

  const tiles = [
    { label: "Events this month", value: data?.bookings_this_month ?? "—", Icon: CalendarDays, tab: "diary" },
    { label: "Enquiries to answer", value: data?.enquiries_to_answer ?? "—", Icon: Mail, tab: "enquiries" },
    { label: "Quotes waiting", value: data?.quotes_waiting ?? "—", Icon: Receipt, tab: "bookings" },
  ];

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {tiles.map((t) => (
          <button key={t.label} data-testid={`kpi-${t.tab}`} onClick={() => onJumpTo(t.tab)} className="text-left rounded-3xl bg-white border border-sand p-6 hover:border-coral transition-colors group">
            <div className="flex items-start justify-between">
              <t.Icon className="w-5 h-5 text-coral" />
              <ArrowRight className="w-4 h-4 text-ocean/40 group-hover:text-coral group-hover:translate-x-0.5 transition-all" />
            </div>
            <p className="font-serif text-5xl text-ocean-deep mt-6 leading-none">{t.value}</p>
            <p className="overline text-[10px] text-ocean/60 mt-3">{t.label}</p>
          </button>
        ))}
      </div>

      {data?.conflicts?.length > 0 && (
        <div data-testid="conflicts-panel" className="rounded-3xl bg-amber-50 border border-amber-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-700" />
            <h3 className="font-serif text-xl text-amber-900">Double-booking alert</h3>
          </div>
          <p className="text-sm text-amber-900/80 mb-4">One bartender, one event per day. You have multiple bookings on:</p>
          <div className="space-y-3">
            {data.conflicts.map((c) => (
              <div key={c.date} className="bg-white rounded-xl border border-amber-200 p-4">
                <p className="font-serif text-lg text-ocean-deep">{fmtDate(c.date)} <span className="text-xs overline text-amber-700 ml-2">{c.count} bookings</span></p>
                <div className="mt-2 space-y-1 text-sm text-ocean/80">
                  {c.bookings.map((b) => (
                    <div key={b.id} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusMeta(b.status).dot }} />
                      {b.customer_name || "—"} · {b.event_type} <span className="text-xs text-ocean/50">({statusMeta(b.status).label})</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-3xl bg-white border border-sand p-6">
        <h3 className="font-serif text-xl text-ocean-deep mb-4">Upcoming events</h3>
        {data?.upcoming?.length ? (
          <div className="space-y-3">
            {data.upcoming.map((b) => {
              const s = statusMeta(b.status);
              return (
                <div key={b.id} className="flex items-center justify-between border-b border-sand pb-3 last:border-0">
                  <div>
                    <p className="font-medium text-ocean-deep">{b.customer_name || "—"} · {b.event_type}</p>
                    <p className="text-xs text-ocean/60">{fmtDate(b.event_date)} · {b.location || "No location"} · {b.guest_count} guests</p>
                  </div>
                  <span className={`text-[10px] overline rounded-full px-3 py-1 ${s.bg} ${s.text}`}>{s.label}</span>
                </div>
              );
            })}
          </div>
        ) : <p className="text-sm text-ocean/50">No upcoming events yet.</p>}
      </div>
    </div>
  );
}
